import {computed, inject, Injectable, signal} from '@angular/core';

import {CSubscription, NotificationService} from '@selpia/app-config/data-access';

@Injectable({
  providedIn: 'root',
})
export class AppNotificationsService {
  readonly #notificationService: NotificationService = inject(NotificationService);
  readonly #applicationServerKey = 'BIt0YCKCQ2iQfrV_PInjWjJRKGKGCPnj2ziFJJDFhRn9HFxuj-jX85tHf5zEGk42sGgXikWSLp7zE3waN1MaYec';

  readonly #subscription = signal<CSubscription | undefined>(undefined);
  readonly isActive = computed(() => this.#subscription()?.isActive);

  constructor() {
    this.#checkSubscription()
      .then((sub) => {
        if (sub) {
          sub.isActive = true;
          this.#subscription.set(sub);
        }
      });
  }

  toggleSubscription(): void {
    if (!this.#subscription()) {
      this.#requestNotificationPermission();
      this.#requestSubscription()
        .then((sub) => {
          if (sub) {
            sub.isActive = true;
            sub.errorMessage = '';
            this.#notificationService.subscribe(sub)
              .subscribe();
            this.#subscription.set(sub);
          } else
            throw new Error('Could not subscribe');
        });
    } else {
      this.#unsubscribe();
      const sub = this.#subscription();
      if (sub && sub.endpoint) {
        this.#notificationService.unsubscribe(sub)
          .subscribe();
        this.#subscription.set(undefined);
      }
    }
  }

  #requestNotificationPermission() {
    return new Promise<void>(function (resolve, reject) {
      Notification.requestPermission(function (result: NotificationPermission) {
        if (result == 'granted') {
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  #checkSubscription() {
    return navigator.serviceWorker.getRegistration()
      .then((reg) => {
        if (reg)
          return reg.pushManager.getSubscription()
            .then((sub) => {
              if (sub) return this.#extractSubscription(sub);
              else return null;
            });
        else return null;
      });
  }

  #requestSubscription() {
    return navigator.serviceWorker.getRegistration()
      .then((reg) => {
        return reg?.pushManager.getSubscription()
          .then((sub) => {
            if (sub) return this.#extractSubscription(sub);
            return reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: this.#applicationServerKey,
            })
              .then((sub) => {
                return this.#extractSubscription(sub);
              });
          });
      });
  }

  #unsubscribe() {
    return navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription()
        .then((subscription) => {
          subscription?.unsubscribe()
            .then();
        });
    });
  }

  #extractSubscription(sub: PushSubscription): CSubscription {
    return new CSubscription({
      endpoint: sub.endpoint,
      p256dh: this.#toBase64(sub.getKey('p256dh')),
      auth: this.#toBase64(sub.getKey('auth')),
    });
  }

  #toBase64(buffer: ArrayBuffer | null): string {
    if (!buffer) return '';
    let binary = '';
    const bytes = new Uint8Array(buffer);

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}
